import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Lock, Crown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { formatFeatureName } from '../utils/featureNames';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FeatureGate Component - Locks features based on subscription tier
 * Shows a full-page upgrade prompt when user doesn't have access to a feature
 */
const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  className = ''
}) => {
  const { user, loading: authLoading } = useAuth();

  // Synchronous admin bypass: if the auth context already knows the user
  // is an admin, never block them or even show the loading spinner. This
  // avoids the "Premium Feature" flash on the first navigation to a gated
  // page that was happening because FeatureGate kicked off its own async
  // API call before the subscription-info response landed.
  const isAdminFromAuth = !authLoading && user?.is_admin === true;

  const [hasAccess, setHasAccess] = useState(isAdminFromAuth ? true : null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    // Admin bypass — short-circuit without hitting the network. We still
    // re-evaluate when auth state changes (e.g. logout + relogin).
    if (isAdminFromAuth) {
      setHasAccess(true);
      return () => {
        isMounted = false;
      };
    }

    // Wait until the auth context has finished restoring the session
    // before deciding access. Without this guard, a transient
    // `user === null` during AuthProvider's initial useEffect would
    // make us call the API with no token and flash the upgrade screen.
    if (authLoading) {
      return () => {
        isMounted = false;
      };
    }

    const checkAccess = async () => {
      try {
        const token = Cookies.get('enakid_token') || localStorage.getItem('enakid_token');
        if (!token) {
          if (isMounted) setHasAccess(false);
          return;
        }

        const response = await axios.get(`${API}/api/user/subscription-info`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isMounted) return;

        const info = response.data;
        setSubscriptionInfo(info);

        // Check if user is admin or has wildcard access or has the specific feature
        const isAdmin = info.is_admin || info.tier === 'admin';
        const hasWildcard = info.features?.includes('*') || info.features?.includes('everything');
        const hasFeatureAccess = info.features?.includes(feature);

        setHasAccess(isAdmin || hasWildcard || hasFeatureAccess);
      } catch (error) {
        console.error('Error checking feature access:', error);
        if (isMounted) setHasAccess(false);
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [feature, authLoading, isAdminFromAuth]);

  // Show loading spinner while checking access
  if (hasAccess === null) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // User has access - render the children
  if (hasAccess) {
    return <>{children}</>;
  }

  // User provided a custom fallback
  if (fallback) {
    return fallback;
  }

  // Default: Full-page upgrade prompt for restricted features
  return (
    <div className={`min-h-[60vh] flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-8 ${className}`}>
      <div className="text-center max-w-md">
        <div className="bg-white shadow-lg rounded-full p-4 inline-block mb-6">
          <Lock className="h-12 w-12 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Premium Feature</h2>
        <p className="text-slate-600 mb-6">
          <strong>{formatFeatureName(feature)}</strong> is not available in your current plan.
        </p>
        {subscriptionInfo && (
          <div className="bg-white rounded-lg p-4 mb-6 border border-slate-200">
            <p className="text-sm text-slate-500 mb-1">Current Plan</p>
            <p className="text-lg font-semibold text-slate-800">
              {subscriptionInfo.tier_display || subscriptionInfo.tier_name || 'Starter'}
            </p>
          </div>
        )}
        <Button 
          onClick={() => navigate('/pricing')} 
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Crown className="h-4 w-4 mr-2" />
          Upgrade Now
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default FeatureGate;
